/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import { type AccountInfo, Configuration, LogLevel } from '@azure/msal-browser'
import { reportErrorToServer } from './services/error-logger'
import { HOME_PAGE } from './types/Constants'
/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_REGISTRATION_CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
    authority: `${import.meta.env.VITE_B2C_AUTHORITY}${import.meta.env.VITE_B2C_SIGNIN_POLICY}`,
    knownAuthorities: [import.meta.env.VITE_B2C_KNOWN_AUTHORITIES],
    redirectUri: '/auth-page', // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
    postLogoutRedirectUri: HOME_PAGE, // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            reportErrorToServer(message)
            break
          default:
            break
        }
      },
    },
  },
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const b2cExtraQueryParameters = {
  environment: import.meta.env.VITE_ENV === 'local' ? 'dev' : import.meta.env.VITE_ENV,
}

export const loginRequest = {
  scopes: [],
  extraQueryParameters: b2cExtraQueryParameters,
}

export const getLoginRequest = (flags: Record<string, boolean>) => {
  const targetMerchantId = new URLSearchParams(window.location.search).get('targetMerchantId')

  // #62287 — kill-switch: if FS is off, ignore targetMerchantId and fall back to standard B2C policy
  if (!targetMerchantId || !flags.EnableMerchantDashboardAccessFromMerchants) return loginRequest

  return {
    ...loginRequest,
    authority: `${import.meta.env.VITE_B2C_AUTHORITY}${import.meta.env.VITE_B2C_FEDERATION_SIGNIN_POLICY}`,
    prompt: 'login',
    extraQueryParameters: {
      ...b2cExtraQueryParameters,
      targetMerchantId,
    },
  }
}

const getPolicy = (account: AccountInfo): string => {
  const claims = account.idTokenClaims as { tfp?: string; acr?: string } | undefined
  return claims?.tfp ?? claims?.acr ?? import.meta.env.VITE_B2C_SIGNIN_POLICY
}

export const getAuthorityForAccount = (account: AccountInfo): string => {
  const policy = getPolicy(account)
  return `${import.meta.env.VITE_B2C_AUTHORITY}${policy}`
}

export const isFederatedUser = (account: AccountInfo): boolean => {
  const policy = getPolicy(account);
  const federationPolicy = import.meta.env.VITE_B2C_FEDERATION_SIGNIN_POLICY
  return !!federationPolicy && policy.toLowerCase() === federationPolicy.toLowerCase()
}

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
export const silentRequest = {
  scopes: ['openid', 'profile'],
  loginHint: 'example@domain.net',
}

export const protectedResources = {
  getTokenAzure: {
    scopes: {
      read: [`${import.meta.env.VITE_B2C_SCOPE_REF}/api://${import.meta.env.VITE_REGISTRATION_CLIENT_ID}/ReadAccess`],
    },
  },
}
